import { useEffect, useState } from "react";
import { useAuditStore } from "../stores/useAuditStore";
import { TopBar } from "../components/layout/TopBar";
import { Spinner } from "../components/shared/Spinner";
import { EmptyState } from "../components/shared/EmptyState";
import * as ui from "../styles/ui";
import { api, type AuditEntry } from "../api/client";
import { formatApiError } from "../lib/api-errors";
import { formatTs } from "../lib/format-ts";

interface Row {
  id: string;
  timestamp: string;
  action: string;
  description: string;
  actor?: string;
  family?: string;
  version?: string;
  source: "server" | "local";
}

export function AuditPage() {
  const localEntries = useAuditStore((s) => s.entries);
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.audit
      .list()
      .then(({ entries }) => {
        if (cancelled) return;
        const serverRows: Row[] = entries.map((e: AuditEntry, i) => ({
          id: `srv-${e.ts}-${i}`,
          timestamp: e.ts,
          action: e.action,
          description: e.message,
          actor: e.actor,
          family: e.family,
          version: e.version,
          source: "server",
        }));
        const localRows: Row[] = localEntries.map((e) => ({
          id: e.id,
          timestamp: e.timestamp,
          action: e.action,
          description: e.description,
          actor: e.actor,
          family: e.family,
          version: e.version,
          source: "local",
        }));
        const merged = [...serverRows, ...localRows].sort(
          (a, b) =>
            new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
        );
        setRows(merged);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(formatApiError(e));
          setRows(
            localEntries.map((e) => ({
              id: e.id,
              timestamp: e.timestamp,
              action: e.action,
              description: e.description,
              actor: e.actor,
              family: e.family,
              version: e.version,
              source: "local" as const,
            })),
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [localEntries]);

  return (
    <>
      <TopBar title="audit" />
      <div style={ui.pageContent}>
        {loading && <Spinner size={22} label="loading…" />}
        {error && <p style={styles.error}>{error}</p>}

        {!loading && rows.length === 0 && (
          <EmptyState title="no activity yet" />
        )}

        {rows.length > 0 && (
          <table style={ui.table}>
            <thead>
              <tr>
                <th style={{ ...ui.thFirst, width: 180 }}>timestamp</th>
                <th style={{ ...ui.th, width: 120 }}>actor</th>
                <th style={{ ...ui.th, width: 100 }}>action</th>
                <th style={ui.th}>description</th>
                <th style={{ ...ui.th, width: 80 }}>version</th>
                <th style={{ ...ui.th, width: 70 }}>source</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((entry) => (
                <tr key={entry.id} style={ui.row}>
                  <td style={styles.tdDate}>{formatTs(entry.timestamp)}</td>
                  <td style={styles.tdMono}>
                    {entry.actor ?? (entry.source === "local" ? "—" : "system")}
                  </td>
                  <td style={styles.td}>
                    <span style={ui.metaText}>{entry.action}</span>
                  </td>
                  <td style={styles.td}>{entry.description}</td>
                  <td style={styles.td}>{entry.version ?? "—"}</td>
                  <td style={styles.tdMono}>{entry.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

const styles: Record<string, React.CSSProperties> = {
  error: ui.errorText,
  td: ui.tdMuted,
  tdDate: { ...ui.tdFirst, ...ui.metaText },
  tdMono: ui.tdMono,
};
